<template>
  <div class="main-panel">
    <!-- Keep existing navbar exactly as-is -->
    <div class="nav-bar-operations" :style="{ backgroundColor: brand.topbarColor }">
      <img :src="brand.topbarLogo" :alt="brand.displayName" class="logo-button" />
    </div>

    <div class="panel-shell">
      <!-- Header / Welcome -->
      <header class="header">
        <div class="header__left">
          <div class="avatar" aria-hidden="true">{{ initials }}</div>
          <div class="greet">
            <p class="eyebrow">Panel principal</p>
            <h1>Bienvenido/a, <span>{{ firstName }}</span></h1>
            <p class="sub">Accede rapido a tus modulos autorizados</p>
          </div>
        </div>
        <div class="header__right">
          <button class="btn btn--ghost" @click="refresh" :disabled="loading" :aria-busy="loading">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 8 8h-2a6 6 0 1 1-6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z" fill="currentColor"/></svg>
            Actualizar
          </button>
          <button class="btn btn--danger" @click="logout">Cerrar sesión</button>
        </div>
      </header>

      <section class="hero-panel">
        <div class="hero-panel__content">
          <h2>Centro de módulos</h2>
          <p>
            Tienes <strong>{{ tiles.length }}</strong> módulos disponibles para tu perfil.
          </p>
        </div>
        <div class="hero-stats">
          <div class="hero-stat">
            <small>Usuario</small>
            <strong>{{ username || firstName || 'Sin nombre' }}</strong>
          </div>
          <div class="hero-stat">
            <small>Accesos</small>
            <strong>{{ tiles.length }}</strong>
          </div>
        </div>
      </section>

      <section class="modules-section">
        <div class="modules-section__head">
          <h3>Módulos disponibles</h3>
          <p>Selecciona un módulo para iniciar tu flujo de trabajo</p>
        </div>

        <section class="module-rows" aria-live="polite" aria-busy="loading">
          <template v-if="!loading && tiles.length">
            <article
              v-for="tile in tiles"
              :key="tile.key"
              :class="['module-row', `module-row--${tile.key}`]"
              role="link"
              tabindex="0"
              @click="go(tile.to)"
              @keyup.enter="go(tile.to)"
            >
              <div class="module-row__accent"></div>
              <div class="module-row__media">
                <img :src="tile.img" :alt="tile.title" />
                <span class="media-chip" v-if="tile.chip">{{ tile.chip }}</span>
              </div>

              <div class="module-row__body">
                <h4>{{ tile.title }}</h4>
                <ul class="module-row__list">
                  <li v-for="(p, i) in tile.points" :key="i">{{ p }}</li>
                </ul>
              </div>

              <div class="module-row__cta">
                <router-link class="btn btn--primary" :to="tile.to">
                  Abrir módulo
                  <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                    <path d="M13.17 6.41 11.76 7.82 15.94 12l-4.18 4.18 1.41 1.41L18.76 12l-5.59-5.59Z" fill="currentColor"/>
                    <path d="M5 11h12v2H5z" fill="currentColor"/>
                  </svg>
                </router-link>
              </div>
            </article>
          </template>

          <!-- Loading skeleton -->
          <template v-else-if="loading">
            <div v-for="n in 6" :key="n" class="skeleton"></div>
          </template>

          <!-- Empty state -->
          <template v-else>
            <div class="empty">
              <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true"><path d="M19 3H5a2 2 0 0 0-2 2v14l4-4h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Z" fill="currentColor"/></svg>
              <h3>No tienes módulos asignados</h3>
              <p>Solicita acceso a un módulo al administrador.</p>
            </div>
          </template>
        </section>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import brand from '@/branding'

export default {
  name: 'MainPanelModern',
  data() {
    return {
      brand,
      username: '',
      firstName: '',
      lastName: '',
      groups: [],
      permissions: [],
      loading: true,
      error: null,
    }
  },
  computed: {
    initials() {
      const f = (this.firstName || '').trim()[0] || ''
      const l = (this.lastName || '').trim()[0] || ''
      return (f + l).toUpperCase() || 'U'
    },
    tiles() {
      const hasPerm = (perm) => {
        if (!perm) return false
        if (this.permissions.includes(perm)) return true
        if (this.permissions.includes(`access_control.${perm}`)) return true
        return false
      }
      const T = []

      if (hasPerm('ui_access_operations_module')) {
        T.push({
          key: 'ops',
          title: 'Operaciones',
          img: require('@/assets/operations-card.jpg'),
          points: ['Órdenes de trabajo', 'Monitoreo de unidades', 'Lotes'],
          to: { path: '/operations-module' },
          chip: 'Core',
        })
      }
      if (hasPerm('ui_access_reports_catalogue')) {
        T.push({
          key: 'reports',
          title: 'Reportes',
          img: require('@/assets/report-card.jpg'),
          points: ['Catálogo general de reportes', 'Papeletas', 'Facturacion'],
          to: { path: '/catalogo-reportes' },
        })
      }
      if (hasPerm('ui_access_maintenance_module')) {
        T.push({
          key: 'mnt',
          title: 'Mantenimiento',
          img: require('@/assets/tools-card.jpg'),
          points: ['Desgaste de unidades', 'Órdenes de mantenimiento', 'Métricas por unidad'],
          to: { path: '/modulo-mantenimiento' },
        })
      }
      if (hasPerm('ui_access_accounting_module')) {
        T.push({
          key: 'acc',
          title: 'Contabilidad',
          img: require('@/assets/contability-card.jpg'),
          points: ['Pagos', 'Facturación', 'Estadísticos'],
          to: { path: '/accounting-module' },
        })
      }
      if (hasPerm('ui_access_rrhh_module')) {
        T.push({
          key: 'hr',
          title: 'Recursos Humanos',
          img: require('@/assets/personal-card.jpg'),
          points: ['Registro de empleados', 'Rendimiento y productividad', 'Gestión de permisos'],
          to: { path: '/modulo-personal' },
        })
      }
      if (hasPerm('ui_access_master_dashboard')) {
        T.push({
          key: 'dash',
          title: 'Dashboard',
          img: require('@/assets/dashboard-card.jpg'),
          points: ['Operaciones', 'Personal', 'Contabilidad'],
          to: { path: '/master-dashboard' },
        })
      }
      if (hasPerm('ui_access_indicadores')) {
        T.push({
          key: 'ind',
          title: 'Indicadores',
          img: require('@/assets/indicadores-card.jpg'),
          points: ['Ventas y servicios', 'SLA / ETA operativo', 'Productividad de unidades'],
          to: { path: '/indicadores' },
          chip: 'Nuevo',
        })
      }
      if (hasPerm('ui_access_logs')) {
        T.push({
          key: 'admin',
          title: 'Admin',
          img: require('@/assets/admin-card.jpg'),
          points: ['Panel de unidades en vivo', 'Visor de logs', 'Monitor de infraestructura TI'],
          to: { path: '/logs' },
        })
      }
      if (hasPerm('ui_access_operator_interface')) {
        T.push({
          key: 'om',
          title: 'Operador',
          img: require('@/assets/operator-card.jpg'),
          points: ['Toma de servicios', 'Registro de inventario', 'Fotografias'],
          to: { path: '/operator-interface' },
        })
      }
      if (hasPerm('ui_access_fuel_module')) {
        T.push({
          key: 'fuel',
          title: 'Combustibles',
          img: require('@/assets/A-vial-card.jpg'),
          points: ['Carga de facturas', 'Captura de cargas de combustible', 'Reportes'],
          to: { path: '/combustibles' },
          chip: 'Nuevo',
        })
      }
      if (hasPerm('ui_access_call_center_module')) {
        T.push({
          key: 'cc',
          title: 'Call Center',
          img: require('@/assets/call-center-card.jpg'),
          points: ['Captura de servicios', 'Monitoreo por capturista', 'Supervisión en tiempo real'],
          to: { path: '/call-center' },
          chip: 'Nuevo',
        })
      }
      if (hasPerm('manage_mobile_app')) {
        T.push({
          key: 'mobile',
          title: 'Aplicación móvil',
          img: require('@/assets/mobile-app-card.jpg'),
          points: ['Instalaciones y vinculación de unidades', 'Notificaciones push'],
          to: { path: '/modulo-movil' },
          chip: 'Nuevo',
        })
      }
      if (hasPerm('maintenance.can_report_bug') || hasPerm('maintenance.is_bug_report_admin')) {
        T.push({
          key: 'help',
          title: 'Ayuda',
          img: require('@/assets/help-card.jpg'),
          points: ['Reportar errores y solicitudes', 'Seguimiento de estatus', 'Comentarios del administrador'],
          to: { path: '/ayuda' },
          chip: 'Nuevo',
        })
      }

      return T
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    prettyGroup(g) {
      const map = {
        operaciones: 'Operaciones',
        mantenimiento: 'Mantenimiento',
        facturacion: 'Contabilidad',
        rh: 'Personal',
        rrhh: 'Personal',
        personal: 'Personal',
        owner: 'Owner',
      }
      return map[g] || g
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      this.error = null
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.username = data.username
        this.firstName = data.first_name
        this.lastName = data.last_name
        this.groups = data.groups || []
        this.permissions = data.permissions || []
        try {
          localStorage.setItem('userPermissions', JSON.stringify(this.permissions))
        } catch (error) {
          // Ignore storage errors
        }
      } catch (err) {
        console.error('Error fetching user:', err)
        this.error = 'No se pudo cargar la información del usuario.'
      } finally {
        this.loading = false
      }
    },
    refresh() {
      this.fetchCurrentUser()
    },
    logout() {
      // Also flush the Django session cookie (used by the OAuth consent
      // flow), so logging out on shared machines really signs out.
      axios.post(`${process.env.API_DIRECTORY}/logout/`).catch(() => {})
      this.$store.commit('setToken', null)
      localStorage.removeItem('authToken')
      this.$router.push({ name: 'UserLogin' })
    },
    go(to) {
      this.$router.push(to)
    },
  },
}
</script>

<style scoped>
.main-panel {
  --bg: #eef2fb;
  --panel: #ffffff;
  --panel-soft: #f5f8ff;
  --muted: #5f6880;
  --text: #0f1b33;
  --brand: #ffc434;
  --brand-ink: #4d3600;
  --primary: #1d4ed8;
  --danger: #dc2626;
  --ring: rgba(29, 78, 216, 0.32);
  --line: rgba(16, 24, 40, 0.11);
  --shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
}

.main-panel {
  min-height: 100vh;
  background:
    radial-gradient(circle at 18% -5%, rgba(255, 196, 52, 0.18), transparent 42%),
    radial-gradient(circle at 82% -12%, rgba(29, 78, 216, 0.17), transparent 40%),
    var(--bg);
  padding: 12vh 24px 48px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  background-color: var(--brand);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}
.logo-button { height: 55%; object-fit: contain; }

.panel-shell {
  max-width: 1240px;
  margin: 0 auto;
}

.header {
  margin-top: 20px;
  padding: 20px 24px;
  border-radius: 20px;
  border: 1px solid var(--line);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(251, 253, 255, 0.98));
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}
.header__left { display: flex; align-items: center; gap: 16px; }
.avatar {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, var(--brand), #ffe188);
  color: var(--brand-ink);
  font-size: 1rem;
  font-weight: 800;
  box-shadow: 0 10px 24px rgba(77, 54, 0, 0.2);
}
.greet .eyebrow {
  margin: 0 0 3px;
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.greet h1 { font-size: 1.25rem; color: var(--text); margin: 0; }
.greet h1 span { font-weight: 700; }
.greet .sub { margin: 4px 0 0; color: var(--muted); font-size: 0.92rem; }
.header__right { display: flex; gap: 10px; }

.hero-panel {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  margin-top: 16px;
  border-radius: 24px;
  border: 1px solid var(--line);
  padding: 22px;
  background:
    linear-gradient(120deg, rgba(255, 255, 255, 0.97), rgba(246, 250, 255, 0.95)),
    var(--panel);
  box-shadow: var(--shadow);
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 16px;
}
.hero-panel::after {
  content: "";
  position: absolute;
  width: 340px;
  height: 340px;
  right: -120px;
  top: -180px;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(29, 78, 216, 0.2) 0%, rgba(29, 78, 216, 0) 70%);
  pointer-events: none;
  z-index: -1;
  animation: heroFloat 9s ease-in-out infinite alternate;
}
.hero-panel h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.55rem;
  line-height: 1.15;
}
.hero-panel p {
  margin: 10px 0 0;
  color: var(--muted);
  max-width: 54ch;
}
.hero-panel p strong { color: var(--text); }

.hero-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.hero-stat {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.hero-stat small {
  color: var(--muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 700;
}
.hero-stat strong {
  color: var(--text);
  font-size: 0.95rem;
}

.modules-section {
  margin-top: 18px;
  border-radius: 24px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: var(--shadow);
  padding: 20px;
}
.modules-section__head h3 {
  margin: 0;
  font-size: 1.15rem;
  color: var(--text);
}
.modules-section__head p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.module-rows {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.module-row {
  --accent: #2d5fe5;
  position: relative;
  display: grid;
  grid-template-columns: 6px 180px 1fr auto;
  align-items: stretch;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
  background: var(--panel);
  outline: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
  opacity: 0;
  transform: translateY(10px);
  animation: rowIn 0.45s ease forwards;
}
.module-row::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, 0.28) 48%, transparent 62%);
  transform: translateX(-120%);
  transition: transform 0.6s ease;
  pointer-events: none;
}
.module-row:hover {
  transform: translateY(-2px);
  border-color: rgba(16, 24, 40, 0.2);
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.13);
}
.module-row:hover::after {
  transform: translateX(130%);
}
.module-row:focus {
  box-shadow: 0 0 0 5px var(--ring), 0 10px 18px rgba(15, 23, 42, 0.12);
}
.module-row__accent {
  background: linear-gradient(180deg, var(--accent), color-mix(in srgb, var(--accent), #ffffff 18%));
}

.module-row__media {
  position: relative;
  min-height: 124px;
}
.module-row__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: saturate(1.05);
  transition: transform 0.45s ease;
}
.module-row:hover .module-row__media img {
  transform: scale(1.04);
}
.module-row__media::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(6, 12, 30, 0.1), rgba(6, 12, 30, 0.4));
}
.media-chip {
  position: absolute;
  z-index: 1;
  top: 10px;
  left: 10px;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 0.68rem;
  font-weight: 700;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.25);
}

.module-row__body {
  padding: 14px 16px;
  display: grid;
  gap: 8px;
  align-content: center;
}
.module-row__body h4 {
  margin: 0;
  color: var(--text);
  font-size: 1.02rem;
}
.module-row__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.module-row__list li {
  background: var(--panel-soft);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 5px 10px;
  color: #37425c;
  font-size: 0.8rem;
  line-height: 1.2;
}

.module-row__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-left: 1px solid var(--line);
  background: #fbfcff;
}

.module-row--ops { --accent: #155dfc; }
.module-row--reports { --accent: #7c3aed; }
.module-row--mnt { --accent: #0f766e; }
.module-row--acc { --accent: #0f766e; }
.module-row--hr { --accent: #c2410c; }
.module-row--dash { --accent: #0f56b3; }
.module-row--ind { --accent: #7c3aed; }
.module-row--admin { --accent: #6b7280; }
.module-row--om { --accent: #b45309; }
.module-row--cc { --accent: #0284c7; }
.module-row--fuel { --accent: #d97706; }
.module-row--mobile { --accent: #ca8a04; }
.module-row--help { --accent: #0f766e; }
.module-row:nth-child(1) { animation-delay: 0.02s; }
.module-row:nth-child(2) { animation-delay: 0.06s; }
.module-row:nth-child(3) { animation-delay: 0.1s; }
.module-row:nth-child(4) { animation-delay: 0.14s; }
.module-row:nth-child(5) { animation-delay: 0.18s; }
.module-row:nth-child(6) { animation-delay: 0.22s; }
.module-row:nth-child(7) { animation-delay: 0.26s; }
.module-row:nth-child(8) { animation-delay: 0.3s; }
.module-row:nth-child(9) { animation-delay: 0.34s; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: var(--panel);
  color: var(--text);
  cursor: pointer;
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}
.btn:focus { outline: none; box-shadow: 0 0 0 5px var(--ring); }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }

.btn--primary { background: var(--primary); color: white; }
.btn--primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(29, 78, 216, 0.34); }

.btn--danger { background: var(--danger); color: white; }
.btn--danger:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3); }

.btn--ghost {
  background: var(--panel);
  border-color: var(--line);
  color: var(--text);
}
.btn--ghost:hover { background: var(--panel-soft); }

.skeleton {
  min-height: 126px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(100deg, rgba(255, 255, 255, 0.42), rgba(255, 255, 255, 0.84), rgba(255, 255, 255, 0.42));
  background-size: 220% 100%;
  animation: shimmer 1.2s infinite linear;
}
@keyframes shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
@keyframes rowIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes heroFloat {
  0% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  100% {
    transform: translate3d(-20px, 18px, 0) scale(1.06);
  }
}

.empty {
  text-align: center;
  color: var(--muted);
  padding: 58px 0;
  border: 1px dashed var(--line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.66);
}
.empty h3 {
  margin: 8px 0 4px;
  color: var(--text);
}

@media (max-width: 1024px) {
  .hero-panel {
    grid-template-columns: 1fr;
  }
  .hero-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .module-rows {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-panel::after,
  .module-row {
    animation: none;
  }
  .module-row,
  .module-row:hover,
  .module-row__media img,
  .btn,
  .module-row::after {
    transition: none;
  }
  .module-row {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 920px) {
  .module-row {
    grid-template-columns: 6px 1fr;
  }
  .module-row__media {
    grid-column: 2;
    min-height: 150px;
  }
  .module-row__body {
    grid-column: 2;
    border-top: 1px solid var(--line);
  }
  .module-row__cta {
    grid-column: 2;
    border-left: 0;
    border-top: 1px solid var(--line);
    justify-content: flex-end;
    padding: 12px 14px;
  }
}

@media (max-width: 720px) {
  .main-panel {
    padding-left: 14px;
    padding-right: 14px;
  }
  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px;
  }
  .header__right { justify-content: space-between; }
  .hero-panel {
    padding: 16px;
  }
  .hero-stats {
    grid-template-columns: 1fr;
  }
  .modules-section {
    padding: 14px;
  }
}

@media screen and (max-height: 586px) {
  .nav-bar-operations { height: 10vh; }
  .main-panel { padding-top: 12vh; }
}
@media screen and (min-height: 930px) {
  .main-panel { padding-top: 14vh; }
}
</style>

